import { ReactNode, createContext, useState } from 'react';

export type TabValue = string | number;

interface ITabsContext {
  value?: TabValue | null;
  setValue: (newValue: TabValue) => void;
}

export const TabsContext = createContext<ITabsContext>({ value: null, setValue: () => null });

/**
 *
 * Controlled provider
 *
 * Useful for when you need/want to do something with the current tab value
 *
 */

export interface IControlledTabsProviderProps {
  children?: ReactNode;
  value: TabValue | null;
  onChange: (newValue: TabValue) => void;
}

export const ControlledTabsProvider = ({
  value,
  onChange,
  children,
}: IControlledTabsProviderProps) => (
  <TabsContext.Provider
    value={{
      value,
      setValue: (newValue) => newValue !== value && onChange(newValue),
    }}
  >
    {children}
  </TabsContext.Provider>
);

/**
 *
 * Uncontrolled provider
 *
 * Useful for when you don't care about the tab value and only care about the functionality
 *
 */

export interface IUncontrolledTabsProviderProps {
  children?: ReactNode;
  initialValue?: TabValue;
}

export const UncontrolledTabsProvider = ({
  initialValue,
  children,
}: IUncontrolledTabsProviderProps) => {
  const [value, setValue] = useState(initialValue ?? null);

  return (
    <TabsContext.Provider
      value={{
        value,
        setValue,
      }}
    >
      {children}
    </TabsContext.Provider>
  );
};
